import React, { useState, useEffect } from 'react';
import {
	ArrowLeftIcon,
	PlusCircleIcon,
	MinusCircleIcon,
	ArrowsRightLeftIcon,
	DocumentTextIcon,
	ListBulletIcon,
	TagIcon,
	BuildingOfficeIcon,
	CubeIcon,
} from '@heroicons/react/24/outline';
import { compareSopVersions } from '../../api';
import { classNames } from '../helpers/functions';
import MarkdownOrHtmlDisplay from '../MarkdownOrHtmlDisplay';

const formatDate = (date) => {
	if (!date) return 'N/A';
	return new Date(date).toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

// Strip HTML tags and markdown formatting for text comparison
const stripFormatting = (content) => {
	if (!content) return '';
	return content
		.replace(/<[^>]*>/g, '') // Strip HTML tags
		.replace(/^#+\s*/gm, '') // Remove markdown headings
		.replace(/\*\*|__/g, '') // Remove bold markers
		.replace(/\*|_/g, '') // Remove italic markers
		.replace(/!\[.*?\]\(.*?\)/g, '') // Remove markdown images
		.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to text
		.trim();
};

// Get first line preview (works with HTML or markdown)
const getFirstLine = (content) => {
	if (!content) return '';
	const text = content
		.replace(/<\/?(p|div|br|h[1-6])[^>]*>/gi, '\n')
		.replace(/<[^>]*>/g, '')
		.replace(/^#+\s*/gm, '') // Remove markdown headings
		.replace(/\*\*|__/g, '') // Remove bold markers
		.replace(/\*|_/g, '') // Remove italic markers
		.trim();
	const firstLine = text.split('\n').find((line) => line.trim()) || '';
	const truncated = firstLine.trim().slice(0, 80);
	return truncated + (firstLine.length > 80 ? '...' : '');
};

/**
 * SopVersionDiff - Side-by-side comparison of two SOP versions
 *
 * @param {Object} props
 * @param {string} props.sopId - The SOP ID
 * @param {number} props.version1 - First version number (older)
 * @param {number} props.version2 - Second version number (newer)
 * @param {Function} props.onBack - Callback to go back to version list
 */
const SopVersionDiff = ({ sopId, version1, version2, onBack }) => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [activeSection, setActiveSection] = useState('all');

	useEffect(() => {
		const fetchComparison = async () => {
			if (!sopId || !version1 || !version2) return;

			setLoading(true);
			setError(null);

			try {
				const result = await compareSopVersions({
					id: sopId,
					version1,
					version2,
				});
				setData(result);
			} catch (err) {
				console.error('Failed to compare versions:', err);
				setError('Failed to load version comparison');
			} finally {
				setLoading(false);
			}
		};

		fetchComparison();
	}, [sopId, version1, version2]);

	if (loading) {
		return (
			<div className="flex items-center justify-center py-8">
				<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
				<span className="ml-2 text-sm text-gray-500">
					Loading comparison...
				</span>
			</div>
		);
	}

	if (error) {
		return (
			<div className="space-y-4">
				<button
					type="button"
					onClick={onBack}
					className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
				>
					<ArrowLeftIcon className="h-4 w-4" />
					Back to version history
				</button>
				<div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
					{error}
				</div>
			</div>
		);
	}

	if (!data) return null;

	const v1 = data.version1;
	const v2 = data.version2;

	// Calculate changes
	const nameChanged = v1.name !== v2.name;
	const descriptionChanged =
		stripFormatting(v1.description) !== stripFormatting(v2.description);
	const categoryChanged = v1.category !== v2.category;
	const stepsChanged = JSON.stringify(v1.steps) !== JSON.stringify(v2.steps);

	const v1MfgNames = (v1.manufacturers || []).map((m) => m.name).sort();
	const v2MfgNames = (v2.manufacturers || []).map((m) => m.name).sort();
	const manufacturersChanged =
		JSON.stringify(v1MfgNames) !== JSON.stringify(v2MfgNames);

	const v1ModelNames = (v1.manufacturerModels || [])
		.map((m) => `${m.manufacturerName} ${m.name}`)
		.sort();
	const v2ModelNames = (v2.manufacturerModels || [])
		.map((m) => `${m.manufacturerName} ${m.name}`)
		.sort();
	const modelsChanged =
		JSON.stringify(v1ModelNames) !== JSON.stringify(v2ModelNames);

	const renderDiffBadge = (changed) => {
		if (!changed) return null;
		return (
			<span className="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
				Changed
			</span>
		);
	};

	const renderValue = (value, changed, side) => {
		if (!changed) {
			return (
				<span className="text-gray-600">
					{value || <em className="text-gray-400">Empty</em>}
				</span>
			);
		}
		return (
			<span
				className={classNames(
					'inline-flex items-center gap-1',
					side === 'old'
						? 'text-red-700 bg-red-50 px-1 rounded'
						: 'text-green-700 bg-green-50 px-1 rounded',
				)}
			>
				{side === 'old' ? (
					<MinusCircleIcon className="h-4 w-4 flex-shrink-0" />
				) : (
					<PlusCircleIcon className="h-4 w-4 flex-shrink-0" />
				)}
				{value || <em className="text-gray-400">Empty</em>}
			</span>
		);
	};

	const shouldShowSection = (sectionId) => {
		if (activeSection === 'all') return true;
		return activeSection === sectionId;
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<button
					type="button"
					onClick={onBack}
					className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
				>
					<ArrowLeftIcon className="h-4 w-4" />
					Back to version history
				</button>
				<div className="flex items-center gap-2 text-sm">
					<span className="font-medium text-red-700">v{version1}</span>
					<ArrowsRightLeftIcon className="h-4 w-4 text-gray-400" />
					<span className="font-medium text-green-700">v{version2}</span>
				</div>
			</div>

			{/* Comparison grid */}
			<div className="border border-gray-200 rounded-lg overflow-hidden">
				{/* Column headers */}
				<div className="grid grid-cols-2 divide-x divide-gray-200 bg-gray-50">
					<div className="px-4 py-3">
						<div className="font-medium text-red-700">Version {version1}</div>
						<div className="text-xs text-gray-500">
							{formatDate(v1.publishedAt)}
						</div>
					</div>
					<div className="px-4 py-3">
						<div className="font-medium text-green-700">Version {version2}</div>
						<div className="text-xs text-gray-500">
							{formatDate(v2.publishedAt)}
						</div>
					</div>
				</div>

				{/* Name */}
				{shouldShowSection('name') && (
					<div className="border-t border-gray-200">
						<div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
							<div className="flex items-center gap-2 text-sm font-medium text-gray-700">
								<DocumentTextIcon className="h-4 w-4" />
								Name
								{renderDiffBadge(nameChanged)}
							</div>
						</div>
						<div className="grid grid-cols-2 divide-x divide-gray-200">
							<div className="px-4 py-3 text-sm">
								{renderValue(v1.name, nameChanged, 'old')}
							</div>
							<div className="px-4 py-3 text-sm">
								{renderValue(v2.name, nameChanged, 'new')}
							</div>
						</div>
					</div>
				)}

				{/* Category */}
				{shouldShowSection('category') && (
					<div className="border-t border-gray-200">
						<div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
							<div className="flex items-center gap-2 text-sm font-medium text-gray-700">
								<TagIcon className="h-4 w-4" />
								Category
								{renderDiffBadge(categoryChanged)}
							</div>
						</div>
						<div className="grid grid-cols-2 divide-x divide-gray-200">
							<div className="px-4 py-3 text-sm capitalize">
								{renderValue(v1.category || 'None', categoryChanged, 'old')}
							</div>
							<div className="px-4 py-3 text-sm capitalize">
								{renderValue(v2.category || 'None', categoryChanged, 'new')}
							</div>
						</div>
					</div>
				)}

				{/* Description */}
				{shouldShowSection('description') && (
					<div className="border-t border-gray-200">
						<div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
							<div className="flex items-center gap-2 text-sm font-medium text-gray-700">
								<DocumentTextIcon className="h-4 w-4" />
								Description
								{renderDiffBadge(descriptionChanged)}
							</div>
						</div>
						<div className="grid grid-cols-2 divide-x divide-gray-200">
							<div className="px-4 py-3 text-sm">
								{v1.description ? (
									<div
										className={classNames(
											descriptionChanged ? 'bg-red-50 p-2 rounded' : '',
										)}
									>
										<MarkdownOrHtmlDisplay content={v1.description} />
									</div>
								) : (
									<em className="text-gray-400">No description</em>
								)}
							</div>
							<div className="px-4 py-3 text-sm">
								{v2.description ? (
									<div
										className={classNames(
											descriptionChanged ? 'bg-green-50 p-2 rounded' : '',
										)}
									>
										<MarkdownOrHtmlDisplay content={v2.description} />
									</div>
								) : (
									<em className="text-gray-400">No description</em>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Steps */}
				{shouldShowSection('steps') && (
					<div className="border-t border-gray-200">
						<div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
							<div className="flex items-center gap-2 text-sm font-medium text-gray-700">
								<ListBulletIcon className="h-4 w-4" />
								Steps ({v1.steps?.length || 0} → {v2.steps?.length || 0})
								{renderDiffBadge(stepsChanged)}
							</div>
						</div>
						<div className="grid grid-cols-2 divide-x divide-gray-200">
							<div className="px-4 py-3">
								{v1.steps?.length > 0 ? (
									<ol className="space-y-2">
										{v1.steps.map((step, idx) => {
											const v2Step = v2.steps?.[idx];
											const changed =
												!v2Step ||
												stripFormatting(step.content) !== stripFormatting(v2Step.content);
											return (
												<li
													key={idx}
													className={classNames(
														'text-sm p-2 rounded',
														changed ? 'bg-red-50' : '',
													)}
												>
													<span className="font-medium text-gray-700">
														Step {idx + 1}:
													</span>{' '}
													{getFirstLine(step.content) || (
														<em className="text-gray-400">Empty step</em>
													)}
												</li>
											);
										})}
									</ol>
								) : (
									<em className="text-gray-400 text-sm">No steps</em>
								)}
							</div>
							<div className="px-4 py-3">
								{v2.steps?.length > 0 ? (
									<ol className="space-y-2">
										{v2.steps.map((step, idx) => {
											const v1Step = v1.steps?.[idx];
											const changed =
												!v1Step ||
												stripFormatting(step.content) !== stripFormatting(v1Step.content);
											return (
												<li
													key={idx}
													className={classNames(
														'text-sm p-2 rounded',
														changed ? 'bg-green-50' : '',
													)}
												>
													<span className="font-medium text-gray-700">
														Step {idx + 1}:
													</span>{' '}
													{getFirstLine(step.content) || (
														<em className="text-gray-400">Empty step</em>
													)}
												</li>
											);
										})}
									</ol>
								) : (
									<em className="text-gray-400 text-sm">No steps</em>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Equipment (Manufacturers & Models) */}
				{shouldShowSection('equipment') && (
					<div className="border-t border-gray-200">
						<div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
							<div className="flex items-center gap-2 text-sm font-medium text-gray-700">
								<BuildingOfficeIcon className="h-4 w-4" />
								Manufacturers
								{renderDiffBadge(manufacturersChanged)}
							</div>
						</div>
						<div className="grid grid-cols-2 divide-x divide-gray-200">
							<div className="px-4 py-3 text-sm">
								{v1.manufacturers?.length > 0 ? (
									<ul className="space-y-1">
										{v1.manufacturers.map((m, idx) => (
											<li
												key={idx}
												className={classNames(
													'px-2 py-1 rounded',
													!v2MfgNames.includes(m.name)
														? 'bg-red-50 text-red-700'
														: '',
												)}
											>
												{m.name}
											</li>
										))}
									</ul>
								) : (
									<em className="text-gray-400">None</em>
								)}
							</div>
							<div className="px-4 py-3 text-sm">
								{v2.manufacturers?.length > 0 ? (
									<ul className="space-y-1">
										{v2.manufacturers.map((m, idx) => (
											<li
												key={idx}
												className={classNames(
													'px-2 py-1 rounded',
													!v1MfgNames.includes(m.name)
														? 'bg-green-50 text-green-700'
														: '',
												)}
											>
												{m.name}
											</li>
										))}
									</ul>
								) : (
									<em className="text-gray-400">None</em>
								)}
							</div>
						</div>

						<div className="px-4 py-2 bg-gray-50 border-t border-b border-gray-200">
							<div className="flex items-center gap-2 text-sm font-medium text-gray-700">
								<CubeIcon className="h-4 w-4" />
								Models
								{renderDiffBadge(modelsChanged)}
							</div>
						</div>
						<div className="grid grid-cols-2 divide-x divide-gray-200">
							<div className="px-4 py-3 text-sm">
								{v1.manufacturerModels?.length > 0 ? (
									<ul className="space-y-1">
										{v1.manufacturerModels.map((m, idx) => {
											const fullName = `${m.manufacturerName} ${m.name}`;
											return (
												<li
													key={idx}
													className={classNames(
														'px-2 py-1 rounded',
														!v2ModelNames.includes(fullName)
															? 'bg-red-50 text-red-700'
															: '',
													)}
												>
													{fullName}
												</li>
											);
										})}
									</ul>
								) : (
									<em className="text-gray-400">None</em>
								)}
							</div>
							<div className="px-4 py-3 text-sm">
								{v2.manufacturerModels?.length > 0 ? (
									<ul className="space-y-1">
										{v2.manufacturerModels.map((m, idx) => {
											const fullName = `${m.manufacturerName} ${m.name}`;
											return (
												<li
													key={idx}
													className={classNames(
														'px-2 py-1 rounded',
														!v1ModelNames.includes(fullName)
															? 'bg-green-50 text-green-700'
															: '',
													)}
												>
													{fullName}
												</li>
											);
										})}
									</ul>
								) : (
									<em className="text-gray-400">None</em>
								)}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default SopVersionDiff;
